/**
 * Trial Expired Page
 * Shown when user's free trial has ended
 * Uses dynamic pricing based on customer count
 */

import { useState, useEffect } from 'react';
import { EDGE_FUNCTIONS_URL } from '@/lib/api-config';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle2, ArrowRight, LogOut, Loader2, Gift } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface TierInfo {
  totalCustomers: number;
  currentTier: 'beta' | 'early90' | 'regular';
  tierName: string;
  price: number;
  spotsRemaining: number | null;
  tierMessage: string;
  couponCode: string | null;
  priceId: string;
  shouldAutoApplyCoupon: boolean;
}

const FEATURES = [
  'Unlimited prospects',
  'Email sequences',
  'LinkedIn automation',
  'Built-in CRM',
  'Analytics dashboard',
  'Email support',
];

export default function TrialExpiredPage() {
  usePageTitle('Trial Expired');
  const { user, signOut } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [tierInfo, setTierInfo] = useState<TierInfo | null>(null);
  const [loadingTier, setLoadingTier] = useState(true);

  // Fetch tier info on component mount
  useEffect(() => {
    fetchTierInfo();
  }, []);

  const fetchTierInfo = async () => {
    try {
      const response = await fetch(`${EDGE_FUNCTIONS_URL}/get-customer-count`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setTierInfo(data);
    } catch (error) {
      console.error('Error fetching tier info:', error);
      toast({
        title: 'Warning',
        description: 'Could not load pricing information',
        variant: 'destructive',
      });
    } finally {
      setLoadingTier(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleUpgrade = async () => {
    if (!user || !tierInfo) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId: tierInfo.priceId,
          userId: user.id,
          customerEmail: user.email,
          couponCode: tierInfo.shouldAutoApplyCoupon ? tierInfo.couponCode : undefined,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to start checkout',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
              <Clock className="h-10 w-10 text-orange-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Your Free Trial Has Ended
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Upgrade now to continue using Prozpector and keep growing your business with powerful automation.
          </p>
        </div>

        {/* Single Pricing Card */}
        {loadingTier ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : tierInfo ? (
          <Card className="border-primary-600 border-2 shadow-2xl bg-white mb-8">
            <CardHeader className="pb-4 text-center">
              {/* Tier Badge */}
              {tierInfo.currentTier === 'beta' && (
                <Badge className="bg-green-600 text-white px-4 py-1 mx-auto mb-4">
                  <Gift className="h-3 w-3 mr-1" />
                  Beta Access - 6 Months FREE
                </Badge>
              )}
              {tierInfo.currentTier === 'early90' && (
                <Badge className="bg-primary-600 text-white px-4 py-1 mx-auto mb-4">
                  Early Adopter Pricing - Locked In Forever
                </Badge>
              )}

              <CardTitle className="text-2xl font-bold text-gray-900">
                {tierInfo.tierName}
              </CardTitle>

              {/* Price */}
              <div className="mt-4">
                {tierInfo.price === 0 ? (
                  <div>
                    <span className="text-5xl font-bold text-green-600">FREE</span>
                    <p className="text-gray-600 mt-2">Use code: <span className="font-mono font-bold text-primary-600">{tierInfo.couponCode}</span></p>
                  </div>
                ) : (
                  <div>
                    <span className="text-5xl font-bold text-gray-900">${tierInfo.price}</span>
                    <span className="text-gray-600 ml-2">/month</span>
                    {tierInfo.currentTier === 'early90' && (
                      <p className="text-sm text-gray-500 mt-1">
                        <span className="line-through">$79/mo</span> — Save $30/mo forever
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Spots remaining */}
              {tierInfo.spotsRemaining !== null && (
                <p className="text-sm text-orange-600 font-medium mt-3">
                  Only {tierInfo.spotsRemaining} spots left at this price!
                </p>
              )}
            </CardHeader>

            <CardContent>
              <Button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full mb-6 bg-primary-600 hover:bg-primary-700 text-lg py-6"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    {tierInfo.price === 0 ? 'Claim Free Access' : 'Upgrade Now'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <ul className="space-y-3">
                {FEATURES.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-gray-200 shadow-lg bg-white mb-8 p-8 text-center">
            <p className="text-gray-600">Unable to load pricing. Please refresh the page.</p>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center space-y-4">
          <p className="text-sm text-gray-500">
            Need help? <a href="mailto:support@prozpector.com" className="text-primary-600 hover:underline">Contact our team</a>
          </p>
          <div>
            <Button
              variant="outline"
              size="default"
              onClick={handleSignOut}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
